import React, { useMemo, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { getFormattedNumber } from "../utils/textUtils";
import Loader from "../Common/Loader";

// Item-wise sales margin. The API returns one row per invoice line with
// purchase and sale price already normalised to the item's open unit.
//
// On screen the rows are grouped by item + purchase price + sale price: selling the same item at the same
// price from the same purchase price shows as ONE row no matter how many invoices it spans. A different
// purchase or sale price makes a new row. Clicking the item name opens a popup with every underlying
// entry (GRN no, purchase date, invoice no, sale date) of that group.

const price = (v) => (v == null ? "NA" : getFormattedNumber(v));
const marginClass = (v) => (v == null ? "" : v < 0 ? "text-danger" : "text-success");
const pct = (v) => (v == null ? "NA" : getFormattedNumber(v) + "%");

// One date when every entry in the group has the same date, otherwise "earliest to latest".
const dateSpan = (entries, field) => {
    const dates = Array.from(new Set(entries.map((e) => e[field]).filter(Boolean))).sort();
    if (dates.length === 0) return "NA";
    if (dates.length === 1) return dates[0];
    return dates[0] + " to " + dates[dates.length - 1];
};

const groupRows = (rows) => {
    const map = new Map();
    rows.forEach((r) => {
        const key = [r.itemName, r.openUnit, r.purchasePrice, r.salePrice].join("|");
        if (!map.has(key)) {
            map.set(key, {
                itemName: r.itemName,
                openUnit: r.openUnit,
                purchasePrice: r.purchasePrice,
                salePrice: r.salePrice,
                marginPercent: r.marginPercent,
                entries: [],
            });
        }
        map.get(key).entries.push(r);
    });
    return Array.from(map.values());
};

const SalesMarginReport = ({ resPacket, loading }) => {
    const [selected, setSelected] = useState(null);
    const groups = useMemo(() => groupRows(resPacket || []), [resPacket]);

    if (loading) {
        return <Loader />;
    }
    if (!resPacket || resPacket.length === 0) {
        return "No Records Found";
    }

    return (
        <>
            <div className="customTable" id="scroll">
                <table className="customTable w-100">
                    <thead className="fixed-thead">
                        <tr>
                            <td>SNO.</td>
                            <td>ITEM NAME</td>
                            <td>UNIT</td>
                            <td>GRN DATE</td>
                            <td className="text-right">PURCHASE PRICE</td>
                            <td>INVOICE DATE</td>
                            <td className="text-right">SALE PRICE</td>
                            <td className="text-right">% OF MARGIN</td>
                            <td className="text-right">NO. OF SALES</td>
                        </tr>
                    </thead>
                    <tbody className="body-scroll-custom">
                        {groups.map((g, i) => (
                            <tr key={i}>
                                <td>{i + 1}</td>
                                <td>
                                    <span
                                        style={{ cursor: "pointer", color: "#1a73e8", textDecoration: "underline" }}
                                        title="Click to see GRN and invoice entries"
                                        onClick={() => setSelected(g)}
                                    >
                                        {g.itemName}
                                    </span>
                                </td>
                                <td>{g.openUnit}</td>
                                <td className="white-space-pre">{dateSpan(g.entries, "purchaseDate")}</td>
                                <td className="text-right">{price(g.purchasePrice)}</td>
                                <td className="white-space-pre">{dateSpan(g.entries, "saleDate")}</td>
                                <td className="text-right">{price(g.salePrice)}</td>
                                <td className={"text-right " + marginClass(g.marginPercent)}>{pct(g.marginPercent)}</td>
                                <td className="text-right">{g.entries.length}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Modal show={!!selected} onHide={() => setSelected(null)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 18 }}>
                        {selected?.itemName}
                        <small className="text-muted ml-3" style={{ fontSize: 13 }}>
                            Purchase {price(selected?.purchasePrice)} / Sale {price(selected?.salePrice)} per {selected?.openUnit}
                            {"  |  "}Margin <span className={marginClass(selected?.marginPercent)}>{pct(selected?.marginPercent)}</span>
                        </small>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <table className="table table-sm table-bordered mb-0" style={{ fontSize: 13 }}>
                        <thead>
                            <tr>
                                <th>SNO.</th>
                                <th>PURCHASE DATE</th>
                                <th>GRN NO</th>
                                <th>GRN WAREHOUSE</th>
                                <th className="text-right">PURCHASE PRICE</th>
                                <th>SALE DATE</th>
                                <th>INVOICE NO</th>
                                <th className="text-right">SALE PRICE</th>
                                <th className="text-right">% OF MARGIN</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(selected?.entries || []).map((e, i) => (
                                <tr key={i}>
                                    <td>{i + 1}</td>
                                    <td className="white-space-pre">{e.purchaseDate || "NA"}</td>
                                    <td className="white-space-pre">{e.grnNo || "NA"}</td>
                                    <td>{e.grnWarehouseTitle || "NA"}</td>
                                    <td className="text-right">{price(e.purchasePrice)}</td>
                                    <td className="white-space-pre">{e.saleDate}</td>
                                    <td className="white-space-pre">{e.invoiceNo}</td>
                                    <td className="text-right">{price(e.salePrice)}</td>
                                    <td className={"text-right " + marginClass(e.marginPercent)}>{pct(e.marginPercent)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default SalesMarginReport;
